import { isDevelopment } from '~/utils/shared/platform'

export default async function browserCache<Input extends any[], Output>(
  {
    name,
    bypass = false,
    args,
    expirationSeconds,
  }: {
    name: string
    bypass?: boolean
    args: Input
    expirationSeconds: number
  },
  run: (...args: Input) => Promise<Output>,
): Promise<Output> {
  if (bypass) {
    return run(...args)
  }

  // Tenta obter o resultado do cache usando a função 'get' existente
  const cachedData = await get(name)

  if (cachedData) {
    // Se o cache retornou dados, significa que estão válidos e não expirados
    return cachedData.value as Output
  }

  // Se não houver cache válido, executa a função original
  const result = await run(...args)
  // Salva o resultado no cache usando a função 'set' existente
  // 'expirationSeconds' está em segundos, 'set' espera 'expirationMs'
  await set(name, result, expirationSeconds * 1000)
  return result
}

export async function set(name: string, value: any, expirationMs: number = 1000 * 60 * 60 * 24) {
  // Default 1 day

  const cache = await caches.open(name.split('.')[0])
  const timestamp = +new Date()
  const expirationTimestamp = timestamp + expirationMs
  const headers = new Headers()
  headers.set('Cache-Control', `max-age=${Math.floor(expirationMs / 1000)}`)
  const response = new Response(JSON.stringify({ timestamp, expirationTimestamp, value }), {
    status: 200,
    headers,
  })
  console.log('Setting cache', name, value, expirationMs, expirationTimestamp)
  cache.put(`/__prix-data-cache/${name}/data`, response)
}

export async function get(name: string): Promise<{
  value: any
  timestamp: number
  expirationTimestamp: number
} | null> {
  const cache = await caches.open(name.split('.')[0])
  const response = await cache.match(`/__prix-data-cache/${name}/data`)
  if (!response) return null

  const data = await response.json()
  if (!data.expirationTimestamp) {
    return null
  }
  if (+new Date() > data.expirationTimestamp) {
    return null
  }
  return {
    value: data.value,
    timestamp: data.timestamp,
    expirationTimestamp: data.expirationTimestamp,
  }
}

export async function clear() {
  const cacheNames = await caches.keys()
  cacheNames.forEach(async name => await caches.delete(name))
}

export async function clearOutdatedOnly() {
  const cacheNames = await caches.keys()

  for (const cacheName of cacheNames) {
    console.log('Clearing cache', cacheName)
    const cache = await caches.open(cacheName)
    let requests = await cache.keys()
    let removedCount = 0

    for (const request of requests) {
      try {
        if (!request.url.endsWith('/data')) {
          continue
        }
        const response = await cache.match(request)
        if (response) {
          const data = await response.json()
          if (data.expirationTimestamp && +new Date() > data.expirationTimestamp) {
            console.log('Deleting outdated cache', request.url)
            await cache.delete(request)
            removedCount++
          }
        }
      } catch (error) {
        console.error(error)
      }
    }

    requests = await cache.keys()
    const itemCount = requests.length
    if (removedCount === itemCount) {
      await caches.delete(cacheName)
    }
  }
}

export async function cachedFunction<Input extends any[], Output>(
  {
    name,
    bypass = false,
    args,
    expirationSeconds,
  }: {
    name: string
    bypass?: boolean
    args: Input
    expirationSeconds: number
  },
  run: (...args: Input) => Promise<Output>,
): Promise<Output | Response> {
  if (bypass) {
    return run(...args)
  }

  const cacheResult = await get(name)
  if (cacheResult !== null) {
    return cacheResult.value
  }

  try {
    const result = await run(...args)
    await set(name, result, expirationSeconds)
    return result
  } catch (err) {
    return new Response(JSON.stringify({}), { status: 500 })
  }
}

export const cacheIsEnabled = !isDevelopment
